import {createContext, useContext, useEffect, useState} from 'react';
import {
    auth,
    createUserWithEmailAndPassword,
    googleAuthProvider,
    onIdTokenChanged,
    signInWithEmailAndPassword,
    signInWithPopup,
    facebookAuthProvider,
    microsoftAuthProvider,
    sendEmailVerification,
    sendPasswordResetEmail,
    applyActionCode,
    checkActionCode,
    confirmPasswordReset,
    verifyPasswordResetCode
} from './Firebase';
import { performSessionCleanup } from './sessionCleanup.js';
import {
    clearDeliberateSignOut, isDeliberateSignOut, isNetworkAuthError, isReauthPending, requestReauth,
} from './sessionExpiry.js';
import { RECONNECTED_EVENT } from '../shell/app-status/appStatus.js';

const AuthContext = createContext();

// A token Firebase could not fetch for want of a network is fetched again after these waits (then every minute), and
// at once when the connection is back. Until then the user keeps the session and the token they had.
const TOKEN_RETRY_DELAYS_MS = [5000, 15000, 30000, 60000];

const readPersistedUser = () => {
    try {
        const raw = sessionStorage.getItem('authUser');
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

export const UserAuth = () => {
    return useContext(AuthContext);
};

export const AuthContextProvider = ({ children }) => {
    // Check for user in this tab's sessionStorage on initial load
    const [user, setUser] = useState(readPersistedUser);

    // False until Firebase has reported its first auth state. `user === null` alone cannot be read
    // as "signed out": the seed above is sessionStorage, which is per-TAB, while Firebase persists
    // the session per-ORIGIN — so a new tab (or any tab whose sessionStorage was cleared) starts
    // with user null and is handed the real session a moment later. A guard that redirects on
    // `!user` without this flag ejects signed-in users during that window.
    const [authResolved, setAuthResolved] = useState(false);

    useEffect(() => {
        // onIdTokenChanged fires on sign-in, sign-out, AND every time Firebase
        // internally rotates the ID token (~every 55 min). Using this instead
        // of onAuthStateChanged keeps user.accessToken in sessionStorage/state
        // fresh automatically, so any components still reading user.accessToken
        // directly (instead of going through the axios interceptor) also get
        // the refreshed token without a manual page refresh.
        // Whose session this tab holds: an unexpected sign-out of that user is an expired session, not a logout.
        let sessionUid = readPersistedUser()?.uid || null;
        let retryTimer = null;
        let retryIndex = 0;

        const syncToken = async (currentUser) => {
            clearTimeout(retryTimer);
            try {
                const accessToken = await currentUser.getIdToken();
                retryIndex = 0;
                const userWithToken = {
                    ...currentUser,
                    accessToken: accessToken
                };
                // Store user info with token in sessionStorage (only affects current tab)
                sessionStorage.setItem('authUser', JSON.stringify(userWithToken));
                setUser(userWithToken);
            } catch (error) {
                if (isNetworkAuthError(error)) {
                    // Offline is not signed out: keep the user and the token they had (the status bar says the
                    // connection is down), and fetch it again shortly. A user without a token would leave every
                    // loader waiting for one.
                    console.warn('Could not refresh the access token (no network); retrying:', error?.code);
                    setUser((prev) => prev || currentUser);
                    const delay = TOKEN_RETRY_DELAYS_MS[Math.min(retryIndex, TOKEN_RETRY_DELAYS_MS.length - 1)];
                    retryIndex += 1;
                    retryTimer = setTimeout(() => {
                        if (auth.currentUser) syncToken(auth.currentUser);
                    }, delay);
                    return;
                }
                // Firebase refused the session (revoked, disabled, password changed): ask the user to sign in
                // again over the page instead of leaving them with a session that cannot load anything.
                console.error('Error getting access token:', error);
                requestReauth(currentUser.uid);
            }
        };

        const onReconnected = () => {
            if (auth.currentUser) syncToken(auth.currentUser);
        };
        window.addEventListener(RECONNECTED_EVENT, onReconnected);

        const unsubscribe = onIdTokenChanged(auth, async (currentUser) => {
            // Every path below resolves the question, including the failures — the flag says
            // "Firebase has answered", not "there is a user".
            setAuthResolved(true);
            if (currentUser) {
                clearDeliberateSignOut();
                sessionUid = currentUser.uid;
                await syncToken(currentUser);
            } else {
                clearTimeout(retryTimer);
                // In playground mode, preserve the mock authUser
                if (sessionStorage.getItem('playgroundMode') === 'true') {
                    return;
                }
                // Firebase ended a session nobody signed out of (revoked, account disabled, this tab's session
                // gone): keep the page and the user, and ask them to sign in again. A logout says so first.
                if (sessionUid && !isDeliberateSignOut()) {
                    requestReauth(sessionUid);
                    return;
                }
                if (isReauthPending()) return;
                sessionUid = null;
                // Remove from sessionStorage when logged out
                sessionStorage.removeItem('authUser');
                setUser(null);
            }
        });

        return () => {
            unsubscribe();
            clearTimeout(retryTimer);
            window.removeEventListener(RECONNECTED_EVENT, onReconnected);
        };
    }, []);

    const signUp = (email, password) => {
        return createUserWithEmailAndPassword(auth, email, password);
    };

    const signInWithEmailPassword = (email, password) => {
        return signInWithEmailAndPassword(auth, email, password);
    };

    const sendVerificationEmail = async (user) => {
        const actionCodeSettings = {
            // Where Firebase's own "Continue" lands after it applies the code — login is the next step.
            url: `${window.location.origin}/account?mode=login`,
            handleCodeInApp: false,
        };
        try {
            await sendEmailVerification(user, actionCodeSettings);
            console.log('Verification email sent successfully');
            return true;
        } catch (error) {
            console.error("Error sending verification email:", error);
            throw error;
        }
    };

    const verifyEmail = async (actionCode) => {
        try {
            const info = await checkActionCode(auth, actionCode);
            if (info.operation !== 'VERIFY_EMAIL') {
                return {
                    success: false,
                    message: `This link is for ${info.operation}, not email verification.`
                };
            }
            await applyActionCode(auth, actionCode);
            if (auth.currentUser) {
                await auth.currentUser.reload();
                const updatedUser = auth.currentUser;
                // Force a new token: the cached one still says email_verified=false, which the
                // join-by-domain endpoints refuse.
                const accessToken = await updatedUser.getIdToken(true);
                const userWithToken = {
                    ...updatedUser,
                    accessToken: accessToken
                };
                setUser(userWithToken);
                sessionStorage.setItem('authUser', JSON.stringify(userWithToken));
            }
            return {
                success: true,
                email: info.data.email,
                message: "Email verified successfully!"
            };
        } catch (error) {

            let errorMessage = "Failed to verify email.";

            switch (error.code) {
                case 'auth/invalid-action-code':
                    if (auth.currentUser && auth.currentUser.emailVerified) {
                        return {
                            success: true,
                            email: auth.currentUser.email,
                            message: "Email is already verified!"
                        };
                    }
                    errorMessage = "Invalid or expired verification link.";
                    break;
                case 'auth/expired-action-code':
                    errorMessage = "Verification link has expired.";
                    break;
                case 'auth/user-disabled':
                    errorMessage = "User account has been disabled.";
                    break;
                case 'auth/user-not-found':
                    errorMessage = "User not found.";
                    break;
                default:
                    errorMessage = error.message || "Failed to verify email.";
            }

            return {
                success: false,
                message: errorMessage
            };
        }
    };

    const resetPassword = async (email) => {
        const actionCodeSettings = {
            url: `${window.location.origin}/reset-password`,
            handleCodeInApp: false,
        };
        try {
            await sendPasswordResetEmail(auth, email, actionCodeSettings);
            return true;
        } catch (error) {
            console.error("Error sending password reset email:", error);
            throw error;
        }
    };

    const verifyResetCode = async (actionCode) => {
        try {
            const email = await verifyPasswordResetCode(auth, actionCode);
            return {
                success: true,
                email: email,
                message: "Password reset code is valid."
            };
        } catch (error) {
            let errorMessage = "Invalid or expired password reset link.";

            switch (error.code) {
                case 'auth/invalid-action-code':
                    errorMessage = "Invalid or expired password reset link.";
                    break;
                case 'auth/expired-action-code':
                    errorMessage = "Password reset link has expired.";
                    break;
                case 'auth/user-disabled':
                    errorMessage = "User account has been disabled.";
                    break;
                case 'auth/user-not-found':
                    errorMessage = "User not found.";
                    break;
                default:
                    errorMessage = error.message || "Invalid password reset link.";
            }

            return {
                success: false,
                message: errorMessage
            };
        }
    };

    const confirmReset = async (actionCode, newPassword) => {
        try {
            await confirmPasswordReset(auth, actionCode, newPassword);
            return {
                success: true,
                message: "Password has been reset successfully!"
            };
        } catch (error) {
            let errorMessage = "Failed to reset password.";

            switch (error.code) {
                case 'auth/invalid-action-code':
                    errorMessage = "Invalid or expired password reset link.";
                    break;
                case 'auth/expired-action-code':
                    errorMessage = "Password reset link has expired.";
                    break;
                case 'auth/weak-password':
                    errorMessage = "Password is too weak. Please choose a stronger password.";
                    break;
                case 'auth/user-disabled':
                    errorMessage = "User account has been disabled.";
                    break;
                case 'auth/user-not-found':
                    errorMessage = "User not found.";
                    break;
                default:
                    errorMessage = error.message || "Failed to reset password.";
            }

            return {
                success: false,
                message: errorMessage
            };
        }
    };

    const signInWithGoogle = async () => {
        try {
            return await signInWithPopup(auth, googleAuthProvider);
        } catch (error) {
            console.error("Error during Google sign-in:", error);
            throw error;
        }
    };

    const signInWithFacebook = async () => {
        try {
            return await signInWithPopup(auth, facebookAuthProvider);
        } catch (error) {
            console.error("Error during Facebook sign-in:", error);
            throw error;
        }
    };

    const signInWithMicrosoft = async () => {
        try {
            return await signInWithPopup(auth, microsoftAuthProvider);
        } catch (error) {
            console.error("Error during Microsoft sign-in:", error);
            throw error;
        }
    };

    const logOut = async () => {
        try {
            // Immediate UI response — clear the context user so protected
            // routes bail out before the onIdTokenChanged listener fires.
            setUser(null);
            // Shared cleanup: purges redux-persist, clears sessionStorage,
            // and calls Firebase signOut. Also reused by the axios response
            // interceptor when a token refresh fails.
            await performSessionCleanup();
            return true;
        } catch (error) {
            console.error("Error during logout:", error);
            throw error;
        }
    };

    const value = {
        user,
        authResolved,
        signInWithEmailPassword,
        signInWithGoogle,
        signInWithFacebook,
        signInWithMicrosoft,
        signUp,
        logOut,
        sendVerificationEmail,
        verifyEmail,
        resetPassword,
        verifyResetCode,
        confirmReset,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};